import { useEffect, useMemo, useRef, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import {
  Friend,
  FriendListParams,
  Message,
  Status,
  Tag,
  friends as friendsApi,
  messages as messagesApi,
  statuses as statusesApi,
  tags as tagsApi,
} from '../lib/api';
import { dateKey, formatDate, formatDateTime, formatHHMM, formatTime, initial } from '../lib/format';

export function FriendsPage() {
  const [friendList, setFriendList] = useState<Friend[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterParams, setFilterParams] = useState<FriendListParams>({});
  const [searchQ, setSearchQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 検索文字列のデバウンス
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilterParams((p) => ({ ...p, q: searchQ || undefined }));
    }, 250);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQ]);

  // 初回ロード
  useEffect(() => {
    Promise.all([statusesApi.list(), tagsApi.list()])
      .then(([s, t]) => {
        setStatuses(s.statuses);
        setAllTags(t.tags);
      })
      .catch((e) => setError(String(e)));
  }, []);

  // 友だち一覧の取得
  useEffect(() => {
    setLoading(true);
    friendsApi
      .list(filterParams)
      .then((res) => {
        setFriendList(res.friends);
        if (!selectedId && res.friends.length > 0) {
          setSelectedId(res.friends[0].line_user_id);
        }
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [filterParams]);

  const selectedFriend = useMemo(
    () => friendList.find((f) => f.line_user_id === selectedId) ?? null,
    [friendList, selectedId],
  );

  function refreshFriends() {
    friendsApi.list(filterParams).then((res) => setFriendList(res.friends));
  }

  function toggleFilter<K extends keyof FriendListParams>(key: K, value: FriendListParams[K]) {
    setFilterParams((p) => ({ ...p, [key]: p[key] === value ? undefined : value }));
  }

  function setStatusFilter(statusId: number | null) {
    setFilterParams((p) => {
      if (statusId === null) {
        const { status_id: _omit, ...rest } = p;
        return rest;
      }
      return { ...p, status_id: String(statusId) };
    });
  }

  function setTagFilter(tagId: number | null) {
    setFilterParams((p) => {
      if (tagId === null) {
        const { tag_id: _omit, ...rest } = p;
        return rest;
      }
      return { ...p, tag_id: String(tagId) };
    });
  }

  function setStateFilter(val: '' | 'unread' | 'bookmarked') {
    setFilterParams((p) => {
      const next: FriendListParams = { ...p };
      delete next.unread_only;
      delete next.bookmarked;
      if (val === 'unread') return { ...next, unread_only: true };
      if (val === 'bookmarked') return { ...next, bookmarked: true };
      return next;
    });
  }

  return (
    <div className="app-shell with-detail">
      <Sidebar />
      <FriendListPane
        loading={loading}
        friends={friendList}
        statuses={statuses}
        tags={allTags}
        selectedId={selectedId}
        onSelect={setSelectedId}
        searchQ={searchQ}
        setSearchQ={setSearchQ}
        filterParams={filterParams}
        setStatusFilter={setStatusFilter}
        setTagFilter={setTagFilter}
        setStateFilter={setStateFilter}
      />
      <ChatPane
        friend={selectedFriend}
        statuses={statuses}
        onUpdated={refreshFriends}
      />
      <DetailPane friend={selectedFriend} statuses={statuses} onUpdated={refreshFriends} />
      {error && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            background: '#FBE7E7',
            color: '#C45656',
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
            zIndex: 200,
          }}
          onClick={() => setError(null)}
        >
          {error} ✕
        </div>
      )}
    </div>
  );
}

// ============================================================
// FriendListPane
// ============================================================
function FriendListPane({
  loading,
  friends,
  statuses,
  tags,
  selectedId,
  onSelect,
  searchQ,
  setSearchQ,
  filterParams,
  setStatusFilter,
  setTagFilter,
  setStateFilter,
}: {
  loading: boolean;
  friends: Friend[];
  statuses: Status[];
  tags: Tag[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  searchQ: string;
  setSearchQ: (q: string) => void;
  filterParams: FriendListParams;
  setStatusFilter: (statusId: number | null) => void;
  setTagFilter: (tagId: number | null) => void;
  setStateFilter: (val: '' | 'unread' | 'bookmarked') => void;
}) {
  const unreadCount = friends.filter((f) => f.is_unread).length;
  const bookmarked = friends.filter((f) => f.is_bookmarked);
  const others = friends.filter((f) => !f.is_bookmarked);

  return (
    <section className="friend-list-pane">
      <div className="list-header">
        <div className="list-title">
          友だち
          <span className="friend-count">
            全 {friends.length} 件 {unreadCount > 0 && `/ ${unreadCount} 未読`}
          </span>
        </div>
        <input
          className="search-box"
          placeholder="名前・メモで検索..."
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
        />

        <div className="filter-row">
          <select
            className="filter-select"
            value={filterParams.unread_only ? 'unread' : filterParams.bookmarked ? 'bookmarked' : ''}
            onChange={(e) => setStateFilter(e.target.value as '' | 'unread' | 'bookmarked')}
          >
            <option value="">状態：すべて</option>
            <option value="unread">未読のみ</option>
            <option value="bookmarked">★ ブックマーク</option>
          </select>

          <select
            className="filter-select"
            value={filterParams.status_id ?? ''}
            onChange={(e) => setStatusFilter(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">ステータス：すべて</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterParams.tag_id ?? ''}
            onChange={(e) => setTagFilter(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">タグ：すべて</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>
                #{t.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {friends.length > 0 && (
        <div className="bulk-bar">
          <span>絞り込み結果：{friends.length}人</span>
          <button
            className="bulk-send-btn"
            disabled
            title="次のフェーズで実装予定"
          >
            この人たちに一斉送信（準備中）
          </button>
        </div>
      )}

      <div className="friend-items">
        {loading && <div className="loading">読み込み中...</div>}
        {!loading && friends.length === 0 && (
          <div className="empty">
            該当する友だちがいません。
            <br />
            条件を変えるか、LINE公式アカウントに登録があれば自動で表示されます。
          </div>
        )}

        {bookmarked.length > 0 && (
          <>
            <div className="section-label">★ ブックマーク</div>
            {bookmarked.map((f) => (
              <FriendItem
                key={f.line_user_id}
                friend={f}
                selected={f.line_user_id === selectedId}
                onSelect={() => onSelect(f.line_user_id)}
              />
            ))}
          </>
        )}
        {others.length > 0 && (
          <>
            {bookmarked.length > 0 && (
              <div className="section-label">最近のやり取り</div>
            )}
            {others.map((f) => (
              <FriendItem
                key={f.line_user_id}
                friend={f}
                selected={f.line_user_id === selectedId}
                onSelect={() => onSelect(f.line_user_id)}
              />
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function FriendItem({
  friend,
  selected,
  onSelect,
}: {
  friend: Friend;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      className={`friend-item ${selected ? 'selected' : ''} ${friend.is_unread ? 'unread' : ''}`}
      onClick={onSelect}
    >
      <span
        className={`bookmark-icon ${friend.is_bookmarked ? '' : 'inactive'}`}
        style={{ pointerEvents: 'none' }}
      >
        ★
      </span>
      <div className="avatar">
        {friend.picture_url ? (
          <img src={friend.picture_url} alt="" />
        ) : (
          initial(friend.display_name)
        )}
      </div>
      <div className="friend-meta">
        <div className="friend-top">
          <div className="friend-name">{friend.display_name}</div>
          <div className="friend-time">
            {formatTime(friend.last_message_at ?? friend.followed_at)}
          </div>
        </div>
        <div className="friend-preview">
          {friend.last_message_preview ?? <em style={{ opacity: 0.6 }}>(まだやり取りがありません)</em>}
          {friend.is_unread && <span className="unread-badge" style={{ marginLeft: 'auto' }}>●</span>}
        </div>
        {(friend.status_name || friend.tags.length > 0) && (
          <div className="friend-status-row">
            {friend.status_name && friend.status_color && (
              <span
                className="status-pill"
                style={{
                  background: friend.status_color + '22',
                  color: friend.status_color,
                }}
              >
                <span className="status-dot" style={{ background: friend.status_color }} />
                {friend.status_name}
              </span>
            )}
            {friend.tags.slice(0, 3).map((t) => (
              <span key={t.id} className="tag-pill">
                #{t.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

// ============================================================
// ChatPane
// ============================================================
function ChatPane({
  friend,
  statuses,
  onUpdated,
}: {
  friend: Friend | null;
  statuses: Status[];
  onUpdated: () => void;
}) {
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // 友だちが変わったら履歴ロード&既読更新
  useEffect(() => {
    if (!friend) {
      setChatMessages([]);
      return;
    }
    setLoadingMsgs(true);
    messagesApi
      .list(friend.line_user_id)
      .then((res) => setChatMessages(res.messages))
      .finally(() => setLoadingMsgs(false));
    if (friend.is_unread) {
      friendsApi.markRead(friend.line_user_id).then(() => onUpdated());
    }
  }, [friend?.line_user_id]);

  // 履歴更新時に最下部スクロール
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [chatMessages]);

  if (!friend) {
    return (
      <section className="chat-pane">
        <div className="chat-empty">友だちを選んでください</div>
      </section>
    );
  }

  async function handleSend() {
    if (!friend || !input.trim() || sending) return;
    setSending(true);
    try {
      const sent = await messagesApi.send(friend.line_user_id, input.trim());
      setChatMessages((prev) => [...prev, sent]);
      setInput('');
      onUpdated();
    } catch (err) {
      alert('送信に失敗しました: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSending(false);
    }
  }

  async function handleStatusChange(statusId: number | null) {
    if (!friend) return;
    await friendsApi.update(friend.line_user_id, { status_id: statusId });
    onUpdated();
  }

  async function handleBookmarkToggle() {
    if (!friend) return;
    await friendsApi.update(friend.line_user_id, { is_bookmarked: !friend.is_bookmarked });
    onUpdated();
  }

  async function handleUnreadToggle() {
    if (!friend) return;
    if (friend.is_unread) {
      await friendsApi.markRead(friend.line_user_id);
    } else {
      await friendsApi.markUnread(friend.line_user_id);
    }
    onUpdated();
  }

  // 日付区切りの追加処理
  const renderedItems: { type: 'date' | 'msg'; key: string; data: Message | string }[] = [];
  let lastKey = '';
  for (const m of chatMessages) {
    const k = dateKey(m.sent_at);
    if (k !== lastKey) {
      renderedItems.push({ type: 'date', key: 'd-' + k, data: k });
      lastKey = k;
    }
    renderedItems.push({ type: 'msg', key: 'm-' + m.id, data: m });
  }

  return (
    <section className="chat-pane">
      <div className="chat-header">
        <div className="avatar">
          {friend.picture_url ? <img src={friend.picture_url} alt="" /> : initial(friend.display_name)}
        </div>
        <div className="chat-header-info">
          <div className="chat-name">{friend.display_name}</div>
          <div className="chat-status">
            登録: {formatDate(friend.followed_at)}
            {friend.inflow_route_name && ` · ${friend.inflow_route_name}`}
          </div>
        </div>
        <div className="chat-actions">
          <StatusDropdown
            friend={friend}
            statuses={statuses}
            onChange={handleStatusChange}
          />
          <button
            className={`icon-btn ${friend.is_bookmarked ? 'bookmark-active' : ''}`}
            onClick={handleBookmarkToggle}
            title={friend.is_bookmarked ? 'ブックマーク解除' : 'ブックマーク登録'}
          >
            {friend.is_bookmarked ? '★' : '☆'}
          </button>
          <button className="icon-btn" onClick={handleUnreadToggle} title="既読/未読">
            {friend.is_unread ? '●' : '✓'}
          </button>
        </div>
      </div>

      <div className="chat-body" ref={bodyRef}>
        {loadingMsgs && <div className="loading">読み込み中...</div>}
        {!loadingMsgs && renderedItems.length === 0 && (
          <div className="chat-empty">メッセージはまだありません</div>
        )}
        {renderedItems.map((item) => {
          if (item.type === 'date') {
            return (
              <div key={item.key} className="date-divider">
                {item.data as string}
              </div>
            );
          }
          const m = item.data as Message;
          return (
            <div key={item.key} className={`msg-row ${m.direction === 'outbound' ? 'me' : 'them'}`}>
              <div>
                <div className="msg-bubble">
                  {m.message_type === 'text' ? m.content : `[${m.message_type}]`}
                </div>
                {m.direction === 'outbound' && <div className="read-mark" style={{ textAlign: 'right' }}>送信済</div>}
              </div>
              <div className="msg-time">{formatHHMM(m.sent_at)}</div>
            </div>
          );
        })}
      </div>

      <div className="chat-footer">
        <div className="input-row">
          <textarea
            className="msg-input"
            placeholder="メッセージを入力..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button
            className="send-btn"
            onClick={handleSend}
            disabled={sending || !input.trim()}
          >
            {sending ? '送信中' : '送信'}
          </button>
        </div>
        <div className="input-hint">⌘+Enter で送信 / 送信時に自動で「既読」になります</div>
      </div>
    </section>
  );
}

// ============================================================
// DetailPane
// ============================================================
function DetailPane({
  friend,
  statuses,
  onUpdated,
}: {
  friend: Friend | null;
  statuses: Status[];
  onUpdated: () => void;
}) {
  const [memo, setMemo] = useState('');
  const [memoTimer, setMemoTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  useEffect(() => {
    setMemo(friend?.memo ?? '');
  }, [friend?.line_user_id]);

  if (!friend) {
    return (
      <aside className="detail-pane">
        <div className="empty" style={{ paddingTop: 80 }}>
          友だちを選択すると<br />詳細が表示されます
        </div>
      </aside>
    );
  }

  function saveMemo(value: string) {
    setMemo(value);
    if (memoTimer) clearTimeout(memoTimer);
    setMemoTimer(
      setTimeout(() => {
        if (!friend) return;
        friendsApi.update(friend.line_user_id, { memo: value }).then(onUpdated);
      }, 600),
    );
  }

  async function addTag(name: string) {
    if (!friend || !name.trim()) return;
    await friendsApi.addTag(friend.line_user_id, { tag_name: name.trim() });
    setTagInput('');
    setShowTagInput(false);
    onUpdated();
  }

  async function removeTag(tagId: number) {
    if (!friend) return;
    await friendsApi.removeTag(friend.line_user_id, tagId);
    onUpdated();
  }

  return (
    <aside className="detail-pane">
      <div className="detail-section">
        <div className="detail-section-title">基本情報</div>
        <div className="detail-row">
          <span className="detail-key">表示名</span>
          <span className="detail-val">{friend.display_name}</span>
        </div>
        <div className="detail-row">
          <span className="detail-key">登録日</span>
          <span className="detail-val">{formatDateTime(friend.followed_at)}</span>
        </div>
        <div className="detail-row">
          <span className="detail-key">userId</span>
          <span className="detail-val" style={{ fontSize: 10 }}>
            {friend.line_user_id.slice(0, 6)}...{friend.line_user_id.slice(-4)}
          </span>
        </div>
      </div>

      {friend.status_name && (
        <div className="detail-section">
          <div className="detail-section-title">対応ステータス</div>
          <span
            className="status-pill"
            style={{
              background: (friend.status_color ?? '#000') + '22',
              color: friend.status_color ?? undefined,
              fontSize: 11,
              padding: '4px 10px',
            }}
          >
            <span className="status-dot" style={{ background: friend.status_color ?? undefined }} />
            {friend.status_name}
          </span>
        </div>
      )}

      {friend.inflow_route_name && (
        <div className="detail-section">
          <div className="detail-section-title">流入経路</div>
          <div style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 6, fontSize: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 2 }}>{friend.inflow_route_name}</div>
            <div style={{ fontSize: 10, color: 'var(--text-sub)' }}>
              チャネル: {friend.inflow_route_channel}
            </div>
          </div>
        </div>
      )}

      <div className="detail-section">
        <div className="detail-section-title">タグ</div>
        <div className="tag-list">
          {friend.tags.map((t) => (
            <span key={t.id} className="tag-pill-removable">
              #{t.name}
              <button className="tag-remove-btn" onClick={() => removeTag(t.id)}>
                ✕
              </button>
            </span>
          ))}
          {showTagInput ? (
            <input
              autoFocus
              className="input"
              style={{ width: 120, fontSize: 11, padding: '4px 8px' }}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addTag(tagInput);
                if (e.key === 'Escape') {
                  setShowTagInput(false);
                  setTagInput('');
                }
              }}
              onBlur={() => {
                setShowTagInput(false);
                setTagInput('');
              }}
              placeholder="タグ名"
            />
          ) : (
            <button className="add-tag-btn" onClick={() => setShowTagInput(true)}>
              + タグ
            </button>
          )}
        </div>
      </div>

      <div className="detail-section">
        <div className="detail-section-title">メモ（社内用）</div>
        <textarea
          className="memo-area"
          value={memo}
          onChange={(e) => saveMemo(e.target.value)}
          placeholder="このお客様についてのメモ..."
        />
      </div>
    </aside>
  );
}

// ============================================================
// StatusDropdown
// ============================================================
function StatusDropdown({
  friend,
  statuses,
  onChange,
}: {
  friend: Friend;
  statuses: Status[];
  onChange: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const current = statuses.find((s) => s.id === friend.status_id) ?? null;

  return (
    <div ref={ref} className="status-dropdown">
      <button className="status-dropdown-btn" onClick={() => setOpen((o) => !o)}>
        <span
          className="status-dot"
          style={{ background: current ? current.color : 'var(--border)' }}
        />
        <span style={{ color: current ? current.color : 'var(--text-mute)' }}>
          {current ? current.name : 'ステータスなし'}
        </span>
        <span className="dropdown-arrow">▾</span>
      </button>
      {open && (
        <div className="status-dropdown-list">
          <button
            className={`status-dropdown-item ${!current ? 'selected' : ''}`}
            onClick={() => { onChange(null); setOpen(false); }}
          >
            <span className="status-dot" style={{ background: 'var(--border)' }} />
            <span style={{ color: 'var(--text-mute)' }}>ステータスなし</span>
          </button>
          {statuses.map((s) => (
            <button
              key={s.id}
              className={`status-dropdown-item ${friend.status_id === s.id ? 'selected' : ''}`}
              onClick={() => { onChange(s.id); setOpen(false); }}
            >
              <span className="status-dot" style={{ background: s.color }} />
              <span style={{ color: s.color }}>{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
